import React from 'react';
import CommonPageHeader from '../../../components/CommonPageHeader/CommonPageHeader';
import PageHelmet from '../../../components/shared/PageHelmet';
import MainFooter from '../../../components/MainFooter/MainFooter';
import NavHeader from '../../../components/NavHeader/NavHeader';
import KeyFeatures from '../KeyFeatures/KeyFeatures';
import OurApproach from '../../Home/OurApproach/OurApproach';
import AdditionalServices from '../AdditionalServices/AdditionalServices';
import { LayoutGrid, Smartphone, Building2, Plug, Atom, Hexagon, Layers, AppWindow, Tablet, Cloud, CreditCard, Gauge, Store, LifeBuoy } from 'lucide-react';
import OurExpertise from '../../../components/OurExpertise/OurExpertise';
import { Link } from 'react-router-dom';

const OurExpertiseData = [
    {
        id: 1,
        title: "Web Application Development",
        icon: <LayoutGrid size="64" strokeWidth='1' />,
        content: "We build fast, accessible web applications with React 19, Angular, and Next.js, styled with Tailwind CSS and shadcn/ui for consistent, modern design systems out of the box."
    },
    {
        id: 2,
        title: "Mobile App Development",
        icon: <Smartphone size="64" strokeWidth='1' />,
        content: "High-performance iOS and Android apps built with React Native and Flutter, plus fully native development when platform-specific performance matters most."
    },
    {
        id: 3,
        title: "Enterprise & SaaS Platforms",
        icon: <Building2 size="64" strokeWidth='1' />,
        content: "From internal enterprise systems to multi-tenant SaaS products, we architect software that scales with your users and your business — on web and mobile."
    },
    {
        id: 4,
        title: "API Integration & Backend Connectivity",
        icon: <Plug size="64" strokeWidth='1' />,
        content: "Seamless integration with your existing APIs, payment gateways, and backend systems, so every product we build fits into your broader stack."
    }
]

const KeyFeaturesData = [
    {
        id: 1,
        title: "React & Next.js Development",
        icon: <Atom size="42" strokeWidth='1' />,
        content: "Production-grade web applications built with React 19 and Next.js for speed and SEO."
    },
    {
        id: 2,
        title: "Angular Development",
        icon: <Hexagon size="42" strokeWidth='1' />,
        content: "Structured, maintainable Angular applications for complex enterprise needs."
    },
    {
        id: 3,
        title: "Tailwind CSS & shadcn/ui",
        icon: <Layers size="42" strokeWidth='1' />,
        content: "Consistent, accessible design systems built with Tailwind CSS and shadcn/ui components."
    },
    {
        id: 4,
        title: "React Native Development",
        icon: <Smartphone size="42" strokeWidth='1' />,
        content: "Cross-platform mobile apps for iOS and Android from a single React Native codebase."
    },
    {
        id: 5,
        title: "Flutter Development",
        icon: <AppWindow size="42" strokeWidth='1' />,
        content: "High-performance, natively compiled apps from a single Flutter codebase."
    },
    {
        id: 6,
        title: "Native Android & iOS",
        icon: <Tablet size="42" strokeWidth='1' />,
        content: "Fully native applications for teams that need maximum platform performance."
    },
    {
        id: 7,
        title: "Enterprise & SaaS Platforms",
        icon: <Cloud size="42" strokeWidth='1' />,
        content: "Multi-tenant SaaS products and enterprise applications designed to scale."
    },
    {
        id: 8,
        title: "Payment & API Integration",
        icon: <CreditCard size="42" strokeWidth='1' />,
        content: "Secure payment gateways and third-party API integrations built into your product."
    }
]

const AdditionalServicesData = [
    {
        id: 1,
        title: "Performance & Core Web Vitals",
        icon: <Gauge size="54" strokeWidth='1' />,
        content: "Optimized load times and Core Web Vitals to keep users engaged and rankings healthy."
    },
    {
        id: 2,
        title: "App Store Optimization",
        icon: <Store size="54" strokeWidth='1' />,
        content: "Listings optimized to improve visibility and downloads on the App Store and Google Play."
    },
    {
        id: 3,
        title: "Ongoing Support & Maintenance",
        icon: <LifeBuoy size="54" strokeWidth='1' />,
        content: "Continuous updates, monitoring, and support after launch, across web and mobile."
    }
]

const OurApproachData = [
    {
        id: 1,
        title: <h3><Link to="/">Discovery</Link></h3>,
        img: "assets/img/project/discover_icon_white.svg",
        content: "Understand your goals through collaborative sessions."
    },
    {
        id: 2,
        title: <h3><Link to="/">Design</Link></h3>,
        img: "assets/img/project/design_icon_white.svg",
        content: "Deliver wireframes, prototypes, and scalable architectures focused on usability and performance."
    },
    {
        id: 3,
        title: <h3><Link to="/">Development</Link></h3>,
        img: "assets/img/project/develop_icon_white.svg",
        content: "Deliver wireframes, prototypes, and scalable architectures focused on usability and performance."
    },
    {
        id: 4,
        title: <h3><Link to="/">Quality Assurance</Link></h3>,
        img: "assets/img/project/define_icon_white.svg",
        content: "Ensure flawless functionality on all devices and platforms."
    },
    {
        id: 5,
        title: <h3><Link to="/">Launch & Support</Link></h3>,
        img: "assets/img/project/deliver_icon_white.svg",
        content: "Seamless deployment, app store submissions, and ongoing maintenance."
    }
]
const bgColors = ["#006ce0", "#001133", "#006ce0", "#001133", "#006ce0"];

const SoftwareProductDevelopment = () => {
    return (
        <>
            <PageHelmet
                pageTitle="Software & Product Development"
                description="Harsoft Solutions is a software development company and web development company building products end-to-end — web applications with React 19, Next.js & Tailwind CSS, and mobile apps with React Native & Flutter."
                keywords="Software Development Company, Web Development Company, Web Application Development, Mobile App Development, React Native Development, SaaS Development"
                path="/software-product-development"
            />

            <NavHeader />
            <main>
                <CommonPageHeader title="Software & Product Development" subtitle="Software & Product Development" />
                <OurExpertise OurExpertiseData={OurExpertiseData} title="Our Expertise" subtitle="Build Fast. Ship Everywhere." content="As a software development company and web development company, we build web applications and mobile apps end-to-end — from React 19, Next.js, and Tailwind CSS on the web to React Native and Flutter on mobile." />
                <KeyFeatures KeyFeaturesData={KeyFeaturesData} />
                <AdditionalServices AdditionalServicesData={AdditionalServicesData} img="assets/img/offerings/Product_Development.jpg" />
                <OurApproach OurApproachData={OurApproachData} title="Our Process" bgColors={bgColors} />
            </main>
            <MainFooter />
        </>
    );
};

export default SoftwareProductDevelopment;
